// fallow-ignore-file complexity -- pre-existing popover JSX, moved verbatim from the delivery-mode banner
import { useEffect, useRef } from 'react';
import type { MouseEvent } from 'react';
import {
  Box,
  Paragraph,
  Panel,
  PanelHeader,
  PanelTitle,
  PanelSection,
  Popover,
  PopoverContent,
  List,
  ListItem,
  Link,
  Strong,
  type PopoverProps,
} from '@customerio/pluma-components/react';
import { activeRouteStore } from '@customerio/hydra-nav-state';
import { useStore } from '@nanostores/react';
import { useAnalytics } from '@/services/analytics';
import { useDeliveryModeState } from './use-delivery-mode-state';
import * as styles from './delivery-mode-popover.css';

type DeliveryModePopoverProps = {
  /** The trigger. Pass a render function to receive the popover's floating context. */
  children: NonNullable<PopoverProps['children']>;
};

/**
 * The hover popover behind the delivery-mode banner and the delivery-mode badge: what the
 * workspace's mode does, plus the next set-up step as a call to action. Ported from Ember
 * `<DeliveryModePopover>`.
 */
export function DeliveryModePopover({ children }: DeliveryModePopoverProps) {
  const { track } = useAnalytics();
  const activeRoute = useStore(activeRouteStore);
  const {
    workspace,
    isBuilder,
    builderMustUpgrade,
    isTestDeliveryMode,
    isDeliveryDisabled,
    isSendingDomainSetUp,
    hasSentTransactional,
    verificationRejected,
    sendingDomainsHref,
    cta,
    buildCtaHref,
  } = useDeliveryModeState();

  const trackPopoverOpenedRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (trackPopoverOpenedRef.current != null) {
        window.clearTimeout(trackPopoverOpenedRef.current);
      }
    };
  }, []);

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      trackPopoverOpenedRef.current = setTimeout(() => {
        track('test_delivery_mode', 'popover_opened', {
          'Route Name': activeRoute,
        });
      }, 750);
    } else {
      if (trackPopoverOpenedRef.current != null) {
        window.clearTimeout(trackPopoverOpenedRef.current);
        trackPopoverOpenedRef.current = null;
      }
    }
  };

  return (
    <Popover
      shouldTriggerOnClick={false}
      shouldTriggerOnHover={true}
      shouldTriggerOnFocus={true}
      onOpenChange={handleOpenChange}
      initialFocus={-1}
    >
      {(popoverContext) => (
        <>
          {typeof children === 'function' ? children(popoverContext) : children}
          <PopoverContent p="0" style={{ maxWidth: 460 }}>
            <Panel maxH="inherit" maxW="inherit" style={{ border: 'none' }}>
              <PanelHeader>
                <PanelTitle data-test-delivery-mode-popover-title>
                  {isTestDeliveryMode && isBuilder ? 'Delivers to test users only' : null}
                  {isTestDeliveryMode && !isBuilder ? 'Send emails to a test address' : null}
                  {isDeliveryDisabled ? "Don't send messages" : null}
                </PanelTitle>
              </PanelHeader>

              <PanelSection>
                {isTestDeliveryMode && isBuilder ? (
                  <Paragraph mb="100">Complete setup to send to your audience</Paragraph>
                ) : null}
                <List data-test-delivery-mode-popover-content styleType="none" gap="100">
                  {isTestDeliveryMode && isBuilder ? (
                    <>
                      <ListItem>
                        <Box
                          className={styles.circle({ variant: isSendingDomainSetUp ? 'success' : 'caution' })}
                          centerBaseline="inline"
                          mr="050"
                          aria-hidden="true"
                        />
                        Verify a{' '}
                        <Link href={sendingDomainsHref} variant="secondary">
                          sending domain
                        </Link>
                      </ListItem>
                      <ListItem>
                        <Box
                          className={styles.circle({ variant: hasSentTransactional ? 'success' : 'caution' })}
                          centerBaseline="inline"
                          mr="050"
                          aria-hidden="true"
                        />
                        Send a test email
                      </ListItem>
                      <ListItem>
                        <Box
                          className={styles.circle({ variant: !builderMustUpgrade ? 'success' : 'caution' })}
                          centerBaseline="inline"
                          mr="050"
                          aria-hidden="true"
                        />
                        {verificationRejected ? (
                          // A rejected account can't add funds; show plain text, not an actionable link.
                          'Add funds'
                        ) : (
                          <Link href="/settings/billing/overview?purchaseModal=account-funds" variant="secondary">
                            Add funds
                          </Link>
                        )}
                      </ListItem>
                    </>
                  ) : null}
                  {isTestDeliveryMode && !isBuilder ? (
                    <>
                      <ListItem>
                        <Box
                          className={styles.circle({ variant: 'caution' })}
                          centerBaseline="inline"
                          mr="050"
                          aria-hidden="true"
                        />
                        All emails are sent to <Strong>{workspace?.delivery_email}</Strong>
                      </ListItem>
                      <ListItem>
                        <Box
                          className={styles.circle({ variant: 'success' })}
                          centerBaseline="inline"
                          mr="050"
                          aria-hidden="true"
                        />
                        All other messages such as in-app, push, and SMS send as normal.
                      </ListItem>
                    </>
                  ) : null}
                  {isDeliveryDisabled ? (
                    <ListItem>
                      <Box
                        className={styles.circle({ variant: 'critical' })}
                        centerBaseline="inline"
                        mr="050"
                        aria-hidden="true"
                      />
                      All message sending is disabled
                    </ListItem>
                  ) : null}
                </List>
              </PanelSection>

              {cta != null && !(isTestDeliveryMode && isBuilder) ? (
                <PanelSection withBorder={true}>
                  {cta.link != null && cta.route != null ? (
                    <Link
                      data-test-delivery-mode-popover-cta
                      href={buildCtaHref(cta)}
                      variant="secondary"
                      onClick={(event: MouseEvent) => {
                        track('test_delivery_mode', 'popover_cta_clicked', {
                          'Route Name': activeRoute,
                          'Action Name': event.currentTarget.textContent?.trim(),
                        });

                        popoverContext.floatingContext.onOpenChange(false);
                      }}
                    >
                      {cta.link}
                    </Link>
                  ) : null}{' '}
                  {cta.text}
                </PanelSection>
              ) : null}
            </Panel>
          </PopoverContent>
        </>
      )}
    </Popover>
  );
}
